import { TtPage, User } from "@/CodegenTypes";

export type TtPageDetailsPage = Pick<
  TtPage,
  | "id"
  | "name"
  | "releaseDate"
  | "createdAt"
  | "enteredFrom"
  | "enteredById"
  | "userId"
  | "username"
  | "calculation"
>;

export type TtPageDetailsUser = Pick<
  User,
  "userId" | "username" | "uplineId" | "lineageIds"
> & {
  status?: User["status"];
  downlines?: TtPageDetailsUser[];
  ttPages?: TtPageDetailsPage[];
};

export type TtPageDetailsRow = TtPageDetailsPage & {
  upline: string;
  uplineName: string;
  account: string;
  username: string;
  amount: number;
  uplineCount: number;
  accountCount: number;
};

type TtPageDetailsRowsOptions = {
  includeDownlines?: boolean;
};

function getTime(value: string | Date) {
  return new Date(value).getTime();
}

function getLineageBranchId(rootUserId: string, lineageIds?: string[]) {
  const rootIndex = lineageIds?.indexOf(rootUserId) ?? -1;
  if (rootIndex < 0) {
    return null;
  }
  return lineageIds?.[rootIndex + 1] ?? null;
}

function getImmediateBranchUser(
  rootUser: TtPageDetailsUser,
  usersById: Map<string, TtPageDetailsUser>,
  pageUserId: string
) {
  if (pageUserId === rootUser.userId) {
    return rootUser;
  }

  let current = usersById.get(pageUserId);
  const visited = new Set<string>();
  while (current && current.userId !== rootUser.userId) {
    if (current.uplineId === rootUser.userId) {
      return current;
    }
    if (!current.uplineId || visited.has(current.userId)) {
      break;
    }
    visited.add(current.userId);
    current = usersById.get(current.uplineId);
  }

  const lineageBranchId = getLineageBranchId(
    rootUser.userId,
    usersById.get(pageUserId)?.lineageIds
  );
  return (lineageBranchId && usersById.get(lineageBranchId)) || rootUser;
}

export function buildTtPageDetailsRows(
  user?: TtPageDetailsUser | null,
  options: TtPageDetailsRowsOptions = {}
) {
  if (!user) {
    return [];
  }

  const includeDownlines = options.includeDownlines ?? false;
  const downlines = user.downlines ?? [];
  const usersById = new Map<string, TtPageDetailsUser>(
    [user, ...downlines].map((downline) => [downline.userId, downline])
  );
  const pages = includeDownlines
    ? user.ttPages ?? []
    : (user.ttPages ?? []).filter((page) => page.userId === user.userId);

  const rows = pages
    .map((page) => {
      const branchUser = getImmediateBranchUser(user, usersById, page.userId);
      const owner = usersById.get(page.userId);
      const amount = page.calculation.unit;

      return {
        ...page,
        upline: branchUser.userId,
        uplineName: branchUser.username,
        account: page.userId,
        username: page.username || owner?.username || "",
        amount,
      };
    })
    .filter((row) => row.amount > 0)
    .sort((rowA, rowB) => {
      return (
        rowA.upline.localeCompare(rowB.upline) ||
        rowA.account.localeCompare(rowB.account) ||
        getTime(rowA.createdAt) - getTime(rowB.createdAt)
      );
    });

  const uplineCounts = new Map<string, number>();
  const accountCounts = new Map<string, number>();
  for (const row of rows) {
    uplineCounts.set(row.upline, (uplineCounts.get(row.upline) ?? 0) + 1);
    accountCounts.set(row.account, (accountCounts.get(row.account) ?? 0) + 1);
  }

  return rows.map((row) => ({
    ...row,
    uplineCount: uplineCounts.get(row.upline) ?? 0,
    accountCount: accountCounts.get(row.account) ?? 0,
  }));
}
